import Axios from 'axios'
import URL from 'src/api/url'
import { renderCard, renderSkeletonCards } from '../shared/card'
import { safeColor } from '../continue-learning'
import { resolvePrimaryColor } from 'src/utils/workspace-logo'

const PREVIEW_PAGE_SIZE = 10

export const fetchCatalog = async ({
  catalogId,
  contentTypes = [],
  tagIds = [],
  filterText = '',
  currentPage = 0,
  pageSize = PREVIEW_PAGE_SIZE,
}) => {
  const response = await Axios.post(URL.CATALOG_ADMIN, {
    catalogId: catalogId || null,
    filterText,
    currentPage,
    pageSize,
    contentTypes,
    tagIds,
  })

  const payload = response.data?.data || response.data || {}
  return {
    items: payload.results || [],
    totalCount: payload.totalCount || 0,
  }
}

export const fetchCatalogTags = async () => {
  try {
    const response = await Axios.get(URL.CATALOG_ADMIN_TAGS)
    return response.data?.data || response.data || []
  } catch (error) {
    console.error('Catalog tag fetch error:', error.response?.data || error.message)
    return []
  }
}

export const saveCatalogOrder = async (catalogId, items) =>
  Axios.put(URL.CATALOG_ORDER.replace('{catalogId}', catalogId), {
    items: items.map(item => ({
      type: item.type,
      courseId: item.courseId ?? null,
      collectionId: item.collectionId ?? null,
    })),
  })

// Tag colours are deliberately left out here - the catalog only needs the name as a filter.
const renderTagGroup = tags =>
  tags.length === 0
    ? ''
    : `<div class="catalog-filters-group">
         <div class="catalog-filters-group-title"><p>Tags</p></div>
         ${tags
           .map(
             tag => `<label class="catalog-filters-option">
                       <input type="checkbox" data-tag-id="${tag.id}" data-cke-ignore-events="true">
                       <p>${tag.name}</p>
                     </label>`,
           )
           .join('')}
       </div>`

const renderSidebar = tags => `
  <div class="catalog-filters">
    <div class="catalog-filters-header">
      <span>FILTERS</span>
      <span class="catalog-filters-clear">CLEAR</span>
    </div>
    <input class="catalog-filters-search" type="text" placeholder="Search the catalog"
           data-cke-ignore-events="true">
    <div class="catalog-filters-group">
      <div class="catalog-filters-group-title"><p>Content type</p></div>
      <label class="catalog-filters-option">
        <input type="checkbox" data-content-type="0" data-cke-ignore-events="true">
        <p>Course</p>
      </label>
      <label class="catalog-filters-option">
        <input type="checkbox" data-content-type="1" data-cke-ignore-events="true">
        <p>Learning path</p>
      </label>
    </div>
    ${renderTagGroup(tags)}
  </div>
`

const SKELETON_ROWS = 2
const MAX_COLUMNS = 5

const EMPTY_ICON = `<svg width="28" height="28" viewBox="0 0 24 24" fill="none">
  <path d="M4 5h6a2 2 0 0 1 2 2v12a2 2 0 0 0-2-2H4V5Zm16 0h-6a2 2 0 0 0-2 2v12a2 2 0 0 1 2-2h6V5Z"
        stroke="currentColor" stroke-width="1.6" stroke-linejoin="round"/>
</svg>`

// What a learner sees in the app when the catalog has nothing published yet. The icon takes the
// page's primary colour from --page-accent (see setPageAccent).
const LIVE_EMPTY_HTML = `
  <div class="catalog-live-empty">
    <div class="catalog-live-empty-icon">${EMPTY_ICON}</div>
    <p class="catalog-live-empty-title">New courses are coming soon</p>
    <p class="catalog-live-empty-desc">Check back shortly to start learning.</p>
  </div>
`

/**
 * Sets the page's primary colour, which the empty catalog's icon and the continue learning
 * block's "Your learning" link are drawn in. It lives on the root element because both blocks
 * are filled in after an async fetch, whenever that lands, and a preset being previewed changes
 * the colour without rebuilding them.
 */
// The Default preset writes its headings as <span style="color:hsl(217,99%,49%)">
// (#015FFB). On landing pages (.mcg-landing-content) they follow the page's
// primary color, as in the learner app. Momentum shares this blue but has its
// own primary color, so its headings keep it.
const MOMENTUM_PRIMARY = '#0453f1'
const DEFAULT_HEADING_STYLE_ID = 'mcg-default-heading-color'
const ensureDefaultHeadingStyle = () => {
  if (document.getElementById(DEFAULT_HEADING_STYLE_ID)) return
  const style = document.createElement('style')
  style.id = DEFAULT_HEADING_STYLE_ID
  style.textContent = `.mcg-landing-content span[style*="hsl(217,99%,49%)"],
.mcg-landing-content span[style*="hsl(217, 99%, 49%)"] {
  color: var(--page-default-heading, hsl(217, 99%, 49%)) !important;
}`
  document.head.appendChild(style)
}

export const setPageAccent = color => {
  // A page on the default color shows the workspace branding's.
  const value = safeColor(resolvePrimaryColor(color))
  const root = document.documentElement.style
  if (value) {
    root.setProperty('--page-accent', value)
  } else {
    root.removeProperty('--page-accent')
  }
  ensureDefaultHeadingStyle()
  if (value && String(value).toLowerCase() !== MOMENTUM_PRIMARY) {
    root.setProperty('--page-default-heading', value)
  } else {
    root.removeProperty('--page-default-heading')
  }
}

const renderResults = (items, priceTagActive, hasFilters = false, live = false) => {
  // A filter that matches nothing says so. An empty catalog shows the app's empty state in the
  // page preview, and two rows of placeholder cards in the editor so the admin still sees the
  // block's layout. Changing the column setting does not rebuild the block, so enough cards for
  // the widest grid are rendered and fitSkeletonRows trims them.
  if (!items.length) {
    if (hasFilters) {
      return `<div class="catalog-empty"><p>Nothing to show here</p></div>`
    }
    return live ? LIVE_EMPTY_HTML : renderSkeletonCards(MAX_COLUMNS * SKELETON_ROWS)
  }

  return `<div class="course-list-wrapper">${items
    .map(item => renderCard(item, priceTagActive))
    .join('')}</div>`
}

/**
 * Keeps the placeholder cards to two rows of the columns actually on screen - the column setting,
 * or fewer where a narrow screen collapses the grid. Reruns when the width or setting changes.
 */
const fitSkeletonRows = content => {
  const grid = content.querySelector('.course-list-wrapper')
  const cards = content.querySelectorAll('.block-skeleton-card')
  if (!grid || !cards.length) return

  const columns = getComputedStyle(grid)
    .gridTemplateColumns.split(' ')
    .filter(Boolean).length
  const limit = Math.max(1, columns) * SKELETON_ROWS

  cards.forEach((card, index) => {
    card.style.display = index < limit ? '' : 'none'
  })
}

/**
 * Wires the preview's search box and content-type checkboxes. CKEditor would otherwise swallow
 * the keystrokes and replace the selected widget, so every event is stopped at the input.
 */
export const initCatalogFilterEvents = (container, { catalogId, priceTagActive }) => {
  if (!container) return

  const searchInput = container.querySelector('.catalog-filters-search')
  const checkboxes = Array.from(
    container.querySelectorAll('[data-content-type]'),
  )
  const tagBoxes = Array.from(container.querySelectorAll('[data-tag-id]'))
  const clearButton = container.querySelector('.catalog-filters-clear')
  const content = container.querySelector('.catalog-content')
  if (!content) return
  const live = content.closest('.catalog-wrapper')?.getAttribute('data-live') === 'true'

  fitSkeletonRows(content)
  // Hiding cards changes the block's height and would notify the observer again in the same
  // frame ("ResizeObserver loop" error), so only width changes count and the work waits a frame.
  if (typeof ResizeObserver !== 'undefined') {
    let lastWidth = content.offsetWidth
    new ResizeObserver(entries => {
      const width = entries[0].contentRect.width
      if (width === lastWidth) return
      lastWidth = width
      requestAnimationFrame(() => fitSkeletonRows(content))
    }).observe(content)
  }

  // The column setting only swaps data-grid on the block, which leaves the width alone.
  const block = content.closest('[data-grid]')
  if (block && typeof MutationObserver !== 'undefined') {
    new MutationObserver(() =>
      requestAnimationFrame(() => fitSkeletonRows(content)),
    ).observe(block, { attributes: true, attributeFilter: ['data-grid'] })
  }

  const swallow = element => {
    if (!element) return
    ;[
      'keydown',
      'keyup',
      'keypress',
      'input',
      'paste',
      'cut',
      'mousedown',
      'click',
      'focus',
      'blur',
    ].forEach(type => {
      element.addEventListener(type, event => event.stopPropagation())
    })
  }

  swallow(searchInput)
  checkboxes.forEach(swallow)
  tagBoxes.forEach(swallow)
  swallow(clearButton)

  let timer = null

  const refresh = () => {
    const filterText = searchInput ? searchInput.value.trim() : ''
    const contentTypes = checkboxes
      .filter(box => box.checked)
      .map(box => Number(box.getAttribute('data-content-type')))
    const tagIds = tagBoxes
      .filter(box => box.checked)
      .map(box => box.getAttribute('data-tag-id'))
    const hasFilters =
      Boolean(filterText) || contentTypes.length > 0 || tagIds.length > 0

    fetchCatalog({ catalogId, filterText, contentTypes, tagIds })
      .then(result => {
        content.innerHTML = renderResults(result.items, priceTagActive, hasFilters, live)
        fitSkeletonRows(content)
      })
      .catch(error => {
        console.error('Catalog filter error:', error.response?.data || error.message)
        content.innerHTML =
          '<div class="error-message">Failed to load catalog</div>'
      })
  }

  if (searchInput) {
    searchInput.addEventListener('input', () => {
      clearTimeout(timer)
      timer = setTimeout(refresh, 300)
    })
  }

  checkboxes.forEach(box => box.addEventListener('change', refresh))
  tagBoxes.forEach(box => box.addEventListener('change', refresh))

  if (clearButton) {
    clearButton.style.cursor = 'pointer'
    clearButton.addEventListener('click', () => {
      if (searchInput) searchInput.value = ''
      ;[...checkboxes, ...tagBoxes].forEach(box => {
        box.checked = false
      })
      refresh()
    })
  }
}

/**
 * Preview of the Catalog block for the admin editor. The learner-facing block is
 * interactive (search, filters, infinite scroll); here we only show the first page plus a
 * non-interactive sketch of the filter sidebar so the admin can judge the layout.
 */
const CreateCatalogList = async (
  grid = '3',
  priceTagActive = false,
  catalogId = '',
  { live = false } = {},
) => {
  let items = []
  try {
    const result = await fetchCatalog({ catalogId })
    items = result.items
  } catch (error) {
    console.error('Catalog fetch error:', error.response?.data || error.message)
  }

  const tags = await fetchCatalogTags()

  return `
    <div class="catalog-wrapper" contenteditable="false"${live ? ' data-live="true"' : ''}>
      ${renderSidebar(tags)}
      <div class="catalog-content">${renderResults(items, priceTagActive, false, live)}</div>
    </div>
  `
}

export default CreateCatalogList
